import React, { ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Grid } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import CustomDialog from "../../../../components/Dialog/CustomDialog";
import CustomButton from "../../../../components/Button/CustomButton";
import { tesaDarkRedError } from "../../../../styles/colors";
import ConfirmationDialogContext from "../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import { InnerFormikProps } from "./interfaces/InnerFormikProps";
import CustomDropdownMultiple from "../../../../components/Input/CustomDropdownMultiple";
import { TableRowView } from "./interfaces/TableRowView";
import UserAction from "../../constants/UserAction";
import LaboratoryNotificationListsApi from "../../../../api/services/LaboratoryNotificationListsApi";
import useEnv from "../../../../hooks/env";
import ErrorSnackbarContext from "../../../../contexts/ErrorSnackbarContext";
import NotificationListAssignableUsersInput from "../LaboratoryPanel/components/MultiSelectionTable/components/TableColumnFilter/NotificationListAssignableUsersInput";

export interface NotificationListDialogProps {
  open: boolean;
  action: UserAction;
  laboratoryId: number;
  notificationLists: TableRowView[];
  handleDialogOpen: (v: boolean) => void;
}

interface FormField {
  id: number;
  name: string;
}

interface FormFieldGroup {
  users: FormField[];
  businessUnits: string[];
  marketSegments: FormField[];
  applicationFields: FormField[];
}

const NotificationsDialog: React.FC<NotificationListDialogProps> = (props) => {
  const { open, action, handleDialogOpen, laboratoryId, notificationLists } = props;

  const { api } = useEnv();
  const notificationListsApi = new LaboratoryNotificationListsApi(api);
  const [initialFormValues, setInitialFormValues] = useState<InnerFormikProps>({
    users: [],
    businessUnits: [],
    marketSegments: [],
    applicationFields: [],
  });

  const formRef = useRef<FormikProps<InnerFormikProps>>(null);
  const assignableUsersRef = useRef<FormikProps<{ users: { id: number; name: string }[] }>>(null);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const extractFields = (lists: any[]) => {
    const result: FormFieldGroup = {
      users: [],
      businessUnits: [],
      marketSegments: [],
      applicationFields: [],
    };
    // eslint-disable-next-line no-restricted-syntax
    for (const list of lists) {
      const { applicationField } = list;
      if (!result.businessUnits.includes(applicationField.marketSegment.businessUnit.name)) {
        result.businessUnits.push(applicationField.marketSegment.businessUnit.name);
      }
      if (!result.marketSegments.some((marketSegment) => marketSegment.id === applicationField.marketSegment.id)) {
        result.marketSegments.push(applicationField.marketSegment);
      }
      if (!result.applicationFields.some((appField) => appField.id === applicationField.id)) {
        result.applicationFields.push(applicationField);
      }
    }

    if (action === UserAction.REMOVE) {
      lists.forEach(({ recipients }) => {
        result.users = result.users.concat(recipients);
      });
    }

    return result;
  };

  const initializeFormValues = (): InnerFormikProps => {
    const { businessUnits, marketSegments, applicationFields, users } = extractFields(notificationLists);

    return {
      businessUnits,
      applicationFields,
      marketSegments: marketSegments.map((marketSegment) => marketSegment.name),
      users,
    };
  };

  const showNotificationListConfirmationDialog = () => {
    if (formRef && formRef.current && formRef.current.dirty) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the notification list will be discarded.",
        confirmText: "Apply",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      }).then((choice) => {
        if (choice === "DEFAULT" && formRef.current?.isValid) {
          handleDialogOpen(false);
          formRef?.current?.handleSubmit();
        } else {
          formRef?.current?.handleReset();
          handleDialogOpen(false);
        }
      });
    } else {
      handleDialogOpen(false);
    }
  };

  const handleOnSubmit = (values: InnerFormikProps) => {
    const recipientIds = values.users.map((user) => user.id);
    const applicationFieldIds = values.applicationFields.map((applicationField) => applicationField.id);

    if (action === UserAction.ADD) {
      notificationListsApi
        .addUsersToNotificationList(laboratoryId, { applicationFieldIds, recipientIds })
        .then(() => showSnackbar("Notification list users added", "success"))
        .catch(handleHttpError)
        .finally(() => handleDialogOpen(false));
    } else if (action === UserAction.REMOVE && formRef && formRef.current) {
      const recipientIdsToRemove = formRef.current.initialValues.users
        .map((user) => user.id)
        .filter((id) => !values.users.map((user) => user.id).includes(id));
      notificationListsApi
        .removeUsersFromNotificationList(laboratoryId, {
          applicationFieldIds,
          recipientIds: Array.from(new Set(recipientIdsToRemove)),
        })
        .then(() => showSnackbar("Notification list users removed", "success"))
        .catch(handleHttpError)
        .finally(() => handleDialogOpen(false));
    }
  };

  useEffect(() => {
    setInitialFormValues(initializeFormValues());
  }, []);

  useLayoutEffect(() => {
    if (action === UserAction.REMOVE) {
      const formWrapper = document.querySelector("#assignable-user-form");
      const input = formWrapper?.querySelector("input");
      if (input) {
        input.readOnly = true;
        input.style.pointerEvents = "none";
      }
    }
  });

  return (
    <CustomDialog fullWidth maxWidth="sm" open={open} onClose={showNotificationListConfirmationDialog}>
      {action === UserAction.ADD ? <h2>ADD USERS</h2> : <h2>REMOVE USERS</h2>}
      <Formik innerRef={formRef} enableReinitialize initialValues={initialFormValues} onSubmit={handleOnSubmit}>
        {(formikProps: FormikProps<InnerFormikProps>): ReactNode => {
          const { values, setFieldValue } = formikProps;

          const uniqueUsers = [...new Set(values.users.map((user) => JSON.stringify(user)))].map((str) =>
            JSON.parse(str),
          );

          return (
            <Form id="assignable-user-form">
              <Grid container direction="column" spacing={3}>
                <Grid item>
                  <NotificationListAssignableUsersInput
                    formRef={assignableUsersRef}
                    disabled={false}
                    laboratoryId={laboratoryId}
                    values={{ users: uniqueUsers }}
                    setFieldValue={setFieldValue}
                  />
                </Grid>
                <Grid item>
                  <CustomDropdownMultiple
                    chips
                    disabled
                    name="businessUnits"
                    labelText="Business Units"
                    options={[]}
                    value={values.businessUnits}
                  />
                </Grid>
                <Grid item>
                  <CustomDropdownMultiple
                    chips
                    disabled
                    name="applicationFields"
                    labelText="Applications"
                    options={[]}
                    value={values.applicationFields.map((applicationField) => applicationField.name)}
                  />
                </Grid>
                <Grid item>
                  <CustomDropdownMultiple
                    chips
                    disabled
                    name="marketSegments"
                    labelText="Market Segments"
                    options={[]}
                    value={values.marketSegments}
                  />
                </Grid>
                <Grid container item direction="row" justify="flex-end">
                  <Box marginRight={2}>
                    <CustomButton text="Revert" type="reset" />
                  </Box>
                  <Box>
                    <CustomButton primary text="Apply" type="submit" />
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default NotificationsDialog;
